import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import type { CareRequestDetailData } from '../services/api';

// 백엔드 API 파라미터와 정확히 일치하는 타입
interface CareRequestApiParams {
    memberNo: number;
    apiKey: string;
    careWhat: number;
    careKind: number;
    matchType: string;
    startDate: string;
    endDate: string;
    startCareTime: string;
    endCareTime: string;
    careDays: string;
    careTime: number;
    sigunguCode: string;
    careArea: string;
    careAreaDetail: string;
    careRoom: string;
    xdVal: string;
    ydVal: string;
    visitArea: string | null;
    visitAreaDetail: string | null;
    ptName: string;
    ptHeight: string | null;
    ptWeight: string | null;
    ptAge: string | null;
    ptGender: number | null;
    ptRanking: number | null;
    ptMoveYn: number | null;
    ptDisease: string | null;
    ptOther: string | null;
    ptMon: string | null;
    ptDay: string | null;
    ptRelation: string | null;
    insuranceCompany: string | null;
    gdName: string | null;
    gdJuminNo: string | null;
    gdPhone: string | null;
    gdRelation: string | null;
    gdName2: string | null;
    gdJuminNo2: string | null;
    gdPhone2: string | null;
    gdRelation2: string | null;
    gdMemberName: string | null;
    gdMemberJuminNo: string | null;
    gdMemberPhone: string | null;
    ptCare: string | null;
    ptStatus: string | null;
    oneDayMoney?: number;
}

// 내부 관리용 FormData 구조
interface CareRequestForm {
    careWhat: number | null;
    careKind: number | null;
    matchType: string;
    startDate: Date | null;
    endDate: Date | null;
    selectedDates: Date[];
    startHour: number;
    startMinute: number;
    endHour: number;
    endMinute: number;
    careTime: number;
    sigunguCode: string;
    careArea: string;
    careAreaDetail: string;
    careRoom: string;
    xdVal: string;
    ydVal: string;
    visitArea: string;
    visitAreaDetail: string;
    ptName: string;
    ptHeight: string;
    ptWeight: string;
    ptAge: string;
    ptGender: number | null;
    ptRanking: number | null;
    ptMoveYn: number | null;
    ptDisease: string;
    ptOther: string;
    ptMon: string;
    ptDay: string;
    ptRelation: string;
    insuranceCompany: string;
    gdName: string;
    gdJuminNo: string;
    gdPhone: string;
    gdRelation: string;
    gdName2: string;
    gdJuminNo2: string;
    gdPhone2: string;
    gdRelation2: string;
    gdMemberName: string;
    gdMemberJuminNo: string;
    gdMemberPhone: string;
    ptCare: string[];
    ptStatus: string[];
    oneDayMoney?: number;
}

const initialFormData: CareRequestForm = {
    careWhat: null,
    careKind: 0,
    matchType: 'B',
    startDate: null,
    endDate: null,
    selectedDates: [],
    startHour: 8,
    startMinute: 0,
    endHour: 16,
    endMinute: 0,
    careTime: 8,
    sigunguCode: '',
    careArea: '',
    careAreaDetail: '',
    careRoom: '',
    visitArea: '',
    visitAreaDetail: '',
    xdVal: '',
    ydVal: '',
    ptName: '',
    ptHeight: '',
    ptWeight: '',
    ptAge: '',
    ptGender: null,
    ptRanking: 0,
    ptMoveYn: null,
    ptDisease: '',
    ptOther: '',
    ptMon: '',
    ptDay: '',
    ptRelation: '',
    insuranceCompany: '',
    gdName: '',
    gdJuminNo: '',
    gdPhone: '',
    gdRelation: '',
    gdName2: '',
    gdJuminNo2: '',
    gdPhone2: '',
    gdRelation2: '',
    gdMemberName: '',
    gdMemberJuminNo: '',
    gdMemberPhone: '',
    ptCare: [],
    ptStatus: [],
    oneDayMoney: 0,
};

interface CareRequestContextType {
    formData: CareRequestForm;
    updateStep1: (data: Partial<CareRequestForm>) => void;
    updateStep2: (data: Partial<CareRequestForm>) => void;
    updateStep3: (data: Partial<CareRequestForm>) => void;
    isValidStep1: () => boolean;
    isValidStep2: () => boolean;
    isValidStep3: () => boolean;
    toServerFormat: (memberNo: number, apiKey: string) => CareRequestApiParams;
    resetForm: () => void;
    loadFromDetail: (detail: CareRequestDetailData) => void; // ⭐ 추가
    isEditMode: boolean;                                      // ⭐ 추가
    editCareNo: number | null;                               // ⭐ 추가
    setEditMode: (careNo: number) => void;                   // ⭐ 추가
    clearEditMode: () => void;                               // ⭐ 추가
}

const CareRequestContext = createContext<CareRequestContextType | undefined>(undefined);

export function CareRequestProvider({ children }: { children: ReactNode }) {
    const [formData, setFormData] = useState<CareRequestForm>(() => {
        const saved = sessionStorage.getItem('careRequestForm');
        if (!saved) return initialFormData;

        try {
            const parsed = JSON.parse(saved);
            return {
                ...parsed,
                startDate: parsed.startDate ? new Date(parsed.startDate) : null,
                endDate: parsed.endDate ? new Date(parsed.endDate) : null,
                selectedDates: parsed.selectedDates?.map((d: string) => new Date(d)) || [],
            };
        } catch (error) {
            console.error('sessionStorage 복원 실패:', error);
            return initialFormData;
        }
    });

    // ⭐ 수정 모드 상태
    const [isEditMode, setIsEditModeState] = useState(false);
    const [editCareNo, setEditCareNo] = useState<number | null>(null);

    useEffect(() => {
        sessionStorage.setItem('careRequestForm', JSON.stringify(formData));
    }, [formData]);

    const updateStep1 = (data: Partial<CareRequestForm>) => {
        setFormData(prev => ({ ...prev, ...data }));
    };

    const updateStep2 = (data: Partial<CareRequestForm>) => {
        setFormData(prev => ({ ...prev, ...data }));
    };

    const updateStep3 = (data: Partial<CareRequestForm>) => {
        setFormData(prev => ({ ...prev, ...data }));
    };

    // ⭐ 수정 모드 진입
    const setEditMode = (careNo: number) => {
        setIsEditModeState(true);
        setEditCareNo(careNo);
    };

    // ⭐ 수정 모드 해제
    const clearEditMode = () => {
        setIsEditModeState(false);
        setEditCareNo(null);
    };

    // ⭐ 상세 데이터를 Context에 로드
    const loadFromDetail = (detail: CareRequestDetailData) => {
        let ptAge = '';
        let ptMon = '';
        let ptDay = '';

        if (detail.ptBirth) {
            const parts = detail.ptBirth.split('.');
            if (parts.length === 3) {
                ptAge = parts[0];
                ptMon = String(parseInt(parts[1]));
                ptDay = String(parseInt(parts[2]));
            }
        } else if (detail.ptAge) {
            ptAge = detail.ptAge;
        }

        // careDays → selectedDates 복원
        const selectedDates: Date[] = [];
        if (detail.careDays && detail.careDays.trim() !== '') {
            detail.careDays.split(',').forEach(dateStr => {
                if (dateStr.trim()) {
                    const cleaned = dateStr.trim().replace(/\./g, '');
                    if (cleaned.length === 8) {
                        const year = parseInt(cleaned.substring(0, 4));
                        const month = parseInt(cleaned.substring(4, 6)) - 1;
                        const day = parseInt(cleaned.substring(6, 8));
                        selectedDates.push(new Date(year, month, day));
                    }
                }
            });
        }

        // startDate, endDate 파싱 ("2024.01.15" → Date)
        const parseDate = (dateStr: string): Date | null => {
            if (!dateStr) return null;
            const cleaned = dateStr.replace(/\./g, '-');
            const date = new Date(cleaned);
            return isNaN(date.getTime()) ? null : date;
        };

        // startCareTime, endCareTime 파싱 ("08:00" → hour, minute)
        const parseTime = (timeStr: string): { hour: number; minute: number } => {
            if (!timeStr) return { hour: 8, minute: 0 };
            const parts = timeStr.split(':');
            return {
                hour: parseInt(parts[0]) || 8,
                minute: parseInt(parts[1]) || 0,
            };
        };

        const startTime = parseTime(detail.startCareTime);
        const endTime = parseTime(detail.endCareTime);

        // ptCare, ptStatus 콤마 구분 → 배열
        const ptCareArr = detail.ptCare
            ? detail.ptCare.split(',').map(s => s.trim()).filter(Boolean)
            : [];
        const ptStatusArr = detail.ptStatus
            ? detail.ptStatus.split(',').map(s => s.trim()).filter(Boolean)
            : [];

        // 가족간병 추가 간병인 복원
        const familyList = detail.familyList || [];
        const gdName = familyList[0]?.gdName || '';
        const gdPhone = familyList[0]?.gdPhone || '';
        const gdRelation = familyList[0]?.gdRelation || '';
        const gdJuminNo = familyList[0]?.gdJuminNo || '';
        const gdName2 = familyList[1]?.gdName || '';
        const gdPhone2 = familyList[1]?.gdPhone || '';
        const gdRelation2 = familyList[1]?.gdRelation || '';
        const gdJuminNo2 = familyList[1]?.gdJuminNo || '';

        setFormData({
            careWhat: detail.careWhat,
            careKind: detail.careKind,
            matchType: detail.matchType || 'B',
            startDate: parseDate(detail.startDate),
            endDate: parseDate(detail.endDate),
            selectedDates,
            startHour: startTime.hour,
            startMinute: startTime.minute,
            endHour: endTime.hour,
            endMinute: endTime.minute,
            careTime: detail.careTime || 8,
            sigunguCode: detail.sigunguCode || '',
            careArea: detail.careArea || '',
            careAreaDetail: detail.careAreaDetail || '',
            careRoom: detail.careRoom || '',
            visitArea: detail.visitArea || '',
            visitAreaDetail: detail.visitAreaDetail || '',
            xdVal: detail.xdVal || '',
            ydVal: detail.ydVal || '',
            ptName: detail.ptName || '',
            ptHeight: detail.ptHeight || '',
            ptWeight: detail.ptWeight || '',
            ptAge,
            ptGender: detail.ptGender,
            ptRanking: detail.ptRanking || 0,
            ptMoveYn: detail.ptMoveYn,
            ptDisease: detail.ptDisease || '',
            ptOther: detail.ptOther || '',
            ptMon,
            ptDay,
            ptRelation: detail.ptRelation || '',
            insuranceCompany: detail.insuranceCompany || '',
            gdName,
            gdJuminNo,
            gdPhone,
            gdRelation,
            gdName2,
            gdJuminNo2,
            gdPhone2,
            gdRelation2,
            gdMemberName: detail.gdMemberName || '',
            gdMemberJuminNo: detail.gdMemberJuminno || '',
            gdMemberPhone: detail.gdMemberPhone || '',
            ptCare: ptCareArr,
            ptStatus: ptStatusArr,
            oneDayMoney: detail.oneDayMoney || 0,
        });
    };

    const isValidStep1 = () => {
        return formData.careWhat !== null;
    };

    const isValidStep2 = () => {
        const hasDate = formData.startDate !== null && formData.endDate !== null;
        const hasPlace = formData.careArea !== '';
        const hasRoom = formData.careWhat === 1 ? formData.careRoom !== '' : true;
        return isValidStep1() && hasDate && hasPlace && hasRoom;
    };

    const isValidStep3 = () => {
        const { careWhat, ptName, ptAge, ptMon, ptDay } = formData;
        if (!isValidStep2()) return false;

        const basicValid = ptName !== '' && ptAge !== '' && ptAge.length === 4 && ptMon !== '' && ptDay !== '';

        if (careWhat === 2) {
            return basicValid &&
                formData.ptRelation !== '' &&
                formData.gdMemberName !== '' &&
                formData.gdMemberJuminNo !== '' &&
                formData.gdMemberPhone !== '';
        } else {
            return basicValid &&
                formData.ptHeight !== '' &&
                formData.ptWeight !== '' &&
                formData.ptGender !== null &&
                formData.ptMoveYn !== null;
        }
    };

    const formatDateToYYYYMMDD = (date: Date | string | null): string => {
        if (!date) return '';
        const dateObj = date instanceof Date ? date : new Date(date);
        if (isNaN(dateObj.getTime())) return '';
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}${month}${day}`;
    };

    const formatTimeToHHmm = (hour: number, minute: number): string => {
        return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    };

    const formatCareDays = (): string => {
        if (formData.careKind === 2) {
            const dates: string[] = [];
            const start = formData.startDate!;
            const end = formData.endDate!;
            const current = new Date(start);
            while (current <= end) {
                dates.push(formatDateToYYYYMMDD(current));
                current.setDate(current.getDate() + 1);
            }
            return dates.join(',');
        } else {
            return formData.selectedDates
                .map(date => formatDateToYYYYMMDD(date))
                .sort()
                .join(',');
        }
    };

    const getFamilyRelationText = (relation: string): string => {
        const relations: { [key: string]: string } = {
            '0': '본인', '1': '남편', '2': '아내', '3': '아버지', '4': '어머니',
            '5': '아들', '6': '딸', '7': '형/누나', '8': '남동생', '9': '여동생',
            '10': '할아버지', '11': '할머니', '12': '외할아버지', '13': '외할머니',
            '14': '손자', '15': '손녀', '16': '외손자', '17': '외손녀',
            '18': '며느리', '19': '사위', '20': '시아버지', '21': '시어머니',
            '22': '장인', '23': '장모', '24': '시누이', '25': '처남',
            '26': '처형', '27': '처제', '28': '기타'
        };
        return relations[relation] || relation;
    };

    const toServerFormat = (memberNo: number, apiKey: string): CareRequestApiParams => {
        const isFamilyCare = formData.careWhat === 2;
        const isCompanion = formData.careWhat === 3;

        return {
            memberNo,
            apiKey,
            careWhat: formData.careWhat!,
            careKind: formData.careKind!,
            matchType: formData.matchType,
            startDate: formatDateToYYYYMMDD(formData.startDate!),
            endDate: formatDateToYYYYMMDD(formData.endDate!),
            startCareTime: formatTimeToHHmm(formData.startHour, formData.startMinute),
            endCareTime: formatTimeToHHmm(formData.endHour, formData.endMinute),
            careDays: formatCareDays(),
            careTime: formData.careTime,
            sigunguCode: formData.sigunguCode,
            careArea: formData.careArea.trim(),
            careAreaDetail: formData.careAreaDetail.trim(),
            careRoom: formData.careRoom.trim(),
            visitArea: isCompanion ? formData.visitArea.trim() || null : null,
            visitAreaDetail: isCompanion ? formData.visitAreaDetail.trim() || null : null,
            xdVal: formData.xdVal,
            ydVal: formData.ydVal,
            ptName: formData.ptName.trim(),
            ptHeight: isFamilyCare ? null : formData.ptHeight.trim() || null,
            ptWeight: isFamilyCare ? null : formData.ptWeight.trim() || null,
            ptAge: formData.ptAge || null,
            ptGender: isFamilyCare ? 2 : formData.ptGender,
            ptRanking: isFamilyCare ? 0 : (formData.ptRanking ?? 0),
            ptMoveYn: isFamilyCare ? 1 : formData.ptMoveYn,
            ptDisease: isFamilyCare ? null : formData.ptDisease.trim() || null,
            ptOther: formData.ptOther.trim() || null,
            ptMon: formData.ptMon,
            ptDay: formData.ptDay,
            ptRelation: isFamilyCare ? getFamilyRelationText(formData.ptRelation) : null,
            insuranceCompany: formData.insuranceCompany.trim() || null,
            gdName: isFamilyCare ? formData.gdName.trim() || null : null,
            gdJuminNo: isFamilyCare ? formData.gdJuminNo.trim() || null : null,
            gdPhone: isFamilyCare ? formData.gdPhone.trim() || null : null,
            gdRelation: isFamilyCare ? getFamilyRelationText(formData.gdRelation) : null,
            gdName2: isFamilyCare ? formData.gdName2.trim() || null : null,
            gdJuminNo2: isFamilyCare ? formData.gdJuminNo2.trim() || null : null,
            gdPhone2: isFamilyCare ? formData.gdPhone2.trim() || null : null,
            gdRelation2: isFamilyCare ? getFamilyRelationText(formData.gdRelation2) : null,
            gdMemberName: isFamilyCare ? formData.gdMemberName.trim() || null : null,
            gdMemberJuminNo: isFamilyCare ? formData.gdMemberJuminNo.trim() || null : null,
            gdMemberPhone: isFamilyCare ? formData.gdMemberPhone.trim() || null : null,
            ptCare: !isFamilyCare && formData.ptCare.length > 0
                ? formData.ptCare.join(',')
                : null,
            ptStatus: !isFamilyCare && formData.ptStatus.length > 0
                ? formData.ptStatus.join(',')
                : null,
        };
    };

    const resetForm = () => {
        setFormData(initialFormData);
        sessionStorage.removeItem('careRequestForm');
    };

    return (
        <CareRequestContext.Provider
            value={{
                formData,
                updateStep1,
                updateStep2,
                updateStep3,
                isValidStep1,
                isValidStep2,
                isValidStep3,
                toServerFormat,
                resetForm,
                loadFromDetail,  // ⭐ 추가
                isEditMode,      // ⭐ 추가
                editCareNo,      // ⭐ 추가
                setEditMode,     // ⭐ 추가
                clearEditMode,   // ⭐ 추가
            }}
        >
            {children}
        </CareRequestContext.Provider>
    );
}

export function useCareRequest() {
    const context = useContext(CareRequestContext);
    if (!context) {
        throw new Error('useCareRequest must be used within CareRequestProvider');
    }
    return context;
}